
import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/types/database";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toast";

export interface RecurringTransactionDialogProps {
  clients: Client[];
  onRefresh: () => void;
  trigger: React.ReactNode;
}

export function RecurringTransactionDialog({ 
  clients, 
  onRefresh, 
  trigger 
}: RecurringTransactionDialogProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [clientId, setClientId] = useState("");
  const [dayOfMonth, setDayOfMonth] = useState("");
  const [category, setCategory] = useState("");
  const [transactionType, setTransactionType] = useState<"income" | "expense">("income");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const resetForm = () => {
    setDescription("");
    setAmount("");
    setClientId("");
    setDayOfMonth("");
    setCategory("");
    setTransactionType("income");
  };
  
  const handleSubmit = async () => {
    // Form validation
    if (!description.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }
    
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast.error("Valor deve ser maior que zero");
      return;
    }
    
    if (!clientId) {
      toast.error("Cliente é obrigatório");
      return;
    }
    
    if (!dayOfMonth || isNaN(Number(dayOfMonth)) || Number(dayOfMonth) < 1 || Number(dayOfMonth) > 31) {
      toast.error("Dia do mês deve ser entre 1 e 31");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("User not authenticated");
      
      const { error } = await supabase
        .from('recurring_transactions')
        .insert({
          description: description.trim(),
          amount: Number(amount),
          client_id: clientId,
          day_of_month: Number(dayOfMonth),
          category: category.trim() || 'Geral',
          transaction_type: transactionType,
          user_id: user.id
        });
        
      if (error) throw error;
      
      toast.success("Cobrança recorrente criada com sucesso!");
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error: any) {
      console.error("Error creating recurring transaction:", error);
      toast.error(`Erro ao criar cobrança recorrente: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nova Cobrança Recorrente</DialogTitle>
          <DialogDescription>
            Configure uma transação para ser gerada automaticamente todo mês.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="transaction-type" className="text-right">
              Tipo
            </Label>
            <Select
              value={transactionType}
              onValueChange={(value: "income" | "expense") => setTransactionType(value)}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Receita</SelectItem>
                <SelectItem value="expense">Despesa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Descrição
            </Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="col-span-3"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="amount" className="text-right">
              Valor
            </Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="col-span-3"
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="client" className="text-right">
              Cliente
            </Label>
            <Select
              value={clientId}
              onValueChange={setClientId}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Selecione o cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="day-of-month" className="text-right">
              Dia do Mês
            </Label>
            <Input
              id="day-of-month"
              type="number"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              className="col-span-3"
              min="1"
              max="31"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category" className="text-right">
              Categoria
            </Label>
            <Input
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Criando..." : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
